import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Academy, Booking, Coach, Lesson, LessonOverride, Student, Term } from '@court-sync/shared';
import { STUDENT_INVITE_QUERY_PARAM, getLessonCoachIds } from '@court-sync/shared';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { academyService } from '../services/academy';
import { fetchOwnerStripeTierQuery } from '../queries/academyOwnerStripeTier';
import { lessonService } from '../services/lesson';
import { termService } from '../services/term';
import { coachService } from '../services/coach';
import { waitlistService } from '../services/waitlist';
import { studentService } from '../services/student';
import { publicBookingService } from '../services/publicBooking';
import { STRIPE_PUBLISHABLE_KEY } from '../config/api';
import toast from 'react-hot-toast';
import type { PublicBookingUserType } from '../pages/public/publicBooking/types';
import type { UserFormData } from '../pages/public/publicBooking/schemas';

/** Camp checkout: pick days (daily rows) vs one full-package booking. */
export type CampBookingMode = 'daily' | 'full';

/** Group/term checkout: full term (with pro-rata) vs individual session drop-ins. */
export type GroupBookingMode = 'full' | 'session';

export type LessonStudentEnrollment = {
  studentId: string;
  firstName: string;
  lastName: string;
  isSelf: boolean;
  sessionDates: string[];
  hasFullTerm: boolean;
};

export interface PublicBookingPersistedSlice {
  academySlug: string | null;
  lessonId: string | null;
  userType: PublicBookingUserType;
  selectedSessionDates: string[];
  /** For `lessonType === 'camp'`: how the user is booking (default daily). */
  campBookingMode: CampBookingMode | null;
  /** For `lessonType === 'group'` with sessionPrice: full term vs drop-in sessions (default full). */
  groupBookingMode: GroupBookingMode | null;
  booking: Booking | null;
  allBookings: Booking[];
  userFormData: UserFormData | null;
  paymentWasMade: boolean;
  waitlistClaimEntryId: string | null;
  /** From ?invitation= — unlocks booking for invitation-only programs */
  invitationToken: string | null;
  /** From ?studentInvite= — links manual roster row when completing signup */
  manualInviteToken: string | null;
}

interface PublicBookingState extends PublicBookingPersistedSlice {
  academy: Academy | null;
  lesson: Lesson | null;
  term: Term | null;
  lessonCoaches: Coach[];
  hasPaymentProcessing: boolean;
  stripePromise: Stripe | null;
  existingStudents: Student[];
  selfStudent: Student | null;
  waitlistStudents: Student[];
  myWaitlistEntries: { lessonId: string; id: string }[];
  lessonOverrides: LessonOverride[];
  lessonStudentEnrollments: LessonStudentEnrollment[];
  isDataLoading: boolean;
  dataError: string | null;

  setUserType: (t: PublicBookingUserType) => void;
  setSelectedSessionDates: (dates: string[]) => void;
  setCampBookingMode: (m: CampBookingMode | null) => void;
  setGroupBookingMode: (m: GroupBookingMode | null) => void;
  setBooking: (booking: Booking, allBookings?: Booking[]) => void;
  updateBooking: (patch: Partial<Booking>) => void;
  setUserFormData: (data: UserFormData | null) => void;
  markPaymentMade: () => void;
  setMyWaitlistEntries: (
    updater: (prev: { lessonId: string; id: string }[]) => { lessonId: string; id: string }[]
  ) => void;

  initialize: (slug: string, lid: string, searchParams: URLSearchParams) => Promise<void>;
  /** Sync `?studentInvite=` into store when browsing public academy / lessons (booking wizard not mounted). */
  captureManualInviteFromSearchParams: (searchParams: URLSearchParams) => void;
  fetchExistingStudents: (academyId: string | undefined, user: { id: string; firstName?: string; lastName?: string } | null) => Promise<void>;
  fetchWaitlistData: (academyId: string | undefined, userId: string | undefined) => Promise<void>;
  fetchLessonStudentEnrollments: (lessonId: string) => Promise<void>;
  reset: () => void;
}

const initialPersisted: PublicBookingPersistedSlice = {
  academySlug: null,
  lessonId: null,
  userType: null,
  selectedSessionDates: [],
  campBookingMode: null,
  groupBookingMode: null,
  booking: null,
  allBookings: [],
  userFormData: null,
  paymentWasMade: false,
  waitlistClaimEntryId: null,
  invitationToken: null,
  manualInviteToken: null,
};

const initialVolatile = {
  academy: null as Academy | null,
  lesson: null as Lesson | null,
  term: null as Term | null,
  lessonCoaches: [] as Coach[],
  hasPaymentProcessing: true,
  stripePromise: null as Stripe | null,
  existingStudents: [] as Student[],
  selfStudent: null as Student | null,
  waitlistStudents: [] as Student[],
  myWaitlistEntries: [] as { lessonId: string; id: string }[],
  lessonOverrides: [] as LessonOverride[],
  lessonStudentEnrollments: [] as LessonStudentEnrollment[],
  isDataLoading: true,
  dataError: null as string | null,
};

export const usePublicBookingStore = create<PublicBookingState>()(
  persist(
    (set, get) => ({
      ...initialPersisted,
      ...initialVolatile,

      setUserType: (userType) => set({ userType }),

      setSelectedSessionDates: (selectedSessionDates) => set({ selectedSessionDates }),

      setCampBookingMode: (campBookingMode) =>
        set({ campBookingMode, selectedSessionDates: [] }),

      setGroupBookingMode: (groupBookingMode) =>
        set({ groupBookingMode, selectedSessionDates: [] }),

      setBooking: (booking, allBookings) =>
        set({
          booking,
          allBookings: allBookings ?? (booking ? [booking] : []),
        }),

      updateBooking: (patch) => {
        const b = get().booking;
        if (!b) return;
        set({ booking: { ...b, ...patch } });
      },

      setUserFormData: (userFormData) => set({ userFormData }),

      markPaymentMade: () => set({ paymentWasMade: true }),

      setMyWaitlistEntries: (updater) =>
        set((state) => ({ myWaitlistEntries: updater(state.myWaitlistEntries) })),

      reset: () =>
        set({
          ...initialPersisted,
          invitationToken: null,
          manualInviteToken: null,
          ...initialVolatile,
        }),

      captureManualInviteFromSearchParams: (searchParams) => {
        const raw = searchParams.get(STUDENT_INVITE_QUERY_PARAM);
        if (raw != null && raw.trim() !== '') set({ manualInviteToken: raw.trim() });
      },

      initialize: async (slug, lid, searchParams) => {
        const prev = get();
        const contextChanged = prev.academySlug !== slug || prev.lessonId !== lid;
        if (contextChanged) {
          const keepInvitation = prev.invitationToken;
          const keepManual = prev.manualInviteToken;
          set({
            ...initialPersisted,
            academySlug: slug,
            lessonId: lid,
            invitationToken: keepInvitation,
            manualInviteToken: keepManual,
            ...initialVolatile,
          });
        } else {
          set({ isDataLoading: true, dataError: null });
        }

        try {
          const academyData = await academyService.getAcademyBySlug(slug);
          const lessonData = await lessonService.getLessonById(lid);

          const loadPromises: Promise<void>[] = [];
          let term: Term | null = null;
          let lessonCoaches: Coach[] = [];

          if (lessonData.termId) {
            loadPromises.push(
              termService
                .getPublicTerms(academyData.id)
                .then((terms) => {
                  term = terms.find((t) => t.id === lessonData.termId) ?? null;
                })
                .catch((err) => console.warn('Failed to load term:', err))
            );
          }

          const lessonCoachIds = getLessonCoachIds(lessonData);
          if (lessonCoachIds.length > 0) {
            loadPromises.push(
              coachService
                .getPublicCoaches(academyData.id)
                .then((coaches) => {
                  const byId = new Map(coaches.map((c) => [c.id, c]));
                  lessonCoaches = lessonCoachIds
                    .map((id) => byId.get(id))
                    .filter((c): c is Coach => c != null);
                })
                .catch((err) => console.warn('Failed to load coaches:', err))
            );
          }

          let hasPaymentProcessing = true;
          try {
            const planEntry = await fetchOwnerStripeTierQuery(slug);
            hasPaymentProcessing = planEntry.hasPaymentProcessing;
          } catch (err) {
            console.warn(
              'Failed to fetch subscription plan, defaulting to payment processing:',
              err
            );
          }

          const stripeConnectReady = !!(
            academyData.stripeConnectAccountId && academyData.onboardingComplete
          );
          hasPaymentProcessing = hasPaymentProcessing && stripeConnectReady;

          if (hasPaymentProcessing) {
            let stripe = get().stripePromise;
            if (!stripe) {
              const stripeKey = STRIPE_PUBLISHABLE_KEY;
              if (!stripeKey) {
                console.error('Stripe publishable key not found in environment variables');
                toast.error('Payment system not configured. Please contact support.');
              } else {
                try {
                  stripe = await loadStripe(stripeKey, {
                    stripeAccount: academyData.stripeConnectAccountId ?? undefined,
                  });
                  set({ stripePromise: stripe });
                } catch (error) {
                  console.error('Failed to load Stripe:', error);
                  toast.error('Failed to load payment system. Please refresh the page.');
                }
              }
            }
          } else {
            set({ stripePromise: null });
          }

          await Promise.all(loadPromises);

          let lessonOverrides: LessonOverride[] = [];
          if (lessonData.termId) {
            try {
              lessonOverrides = await lessonService.getPublicLessonOverrides(lid, academyData.id);
            } catch (err) {
              console.warn('Failed to load lesson overrides:', err);
            }
          }

          let dataError: string | null = null;
          const claimEntryId = searchParams.get('entryId');
          const isClaimFlow = searchParams.get('waitlistClaim') === '1' && claimEntryId;
          const invitationFromUrl = searchParams.get('invitation');
          /** Keep token when wizard routes omit ?invitation= (register/details/payment paths). */
          const invitationTokenResolved =
            invitationFromUrl != null && invitationFromUrl !== ''
              ? invitationFromUrl
              : get().invitationToken ?? null;
          const manualInviteFromUrl = searchParams.get(STUDENT_INVITE_QUERY_PARAM);
          const manualInviteTokenResolved =
            manualInviteFromUrl != null && manualInviteFromUrl !== ''
              ? manualInviteFromUrl
              : get().manualInviteToken ?? null;
          let nextWaitlistClaimId = get().waitlistClaimEntryId;

          if (isClaimFlow && lessonData) {
            try {
              const claimRes = await waitlistService.validateWaitlistClaim(claimEntryId, lessonData.id);
              if (claimRes.success && claimRes.data) {
                nextWaitlistClaimId = claimEntryId;
                toast.success('Your waitlist spot is reserved! Complete the booking to claim it.');
              } else {
                dataError =
                  (claimRes as { error?: string }).error ||
                  'This waitlist claim is no longer valid or has expired.';
              }
            } catch {
              dataError = 'This waitlist claim is no longer valid or has expired.';
            }
          }

          set({
            academy: academyData,
            lesson: lessonData,
            term,
            lessonCoaches,
            lessonOverrides,
            hasPaymentProcessing,
            waitlistClaimEntryId: nextWaitlistClaimId,
            invitationToken: invitationTokenResolved,
            manualInviteToken: manualInviteTokenResolved,
            dataError,
            isDataLoading: false,
          });
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to load lesson data';
          set({ dataError: message, isDataLoading: false });
        }
      },

      fetchExistingStudents: async (academyId: string | undefined, user: { id: string; firstName?: string; lastName?: string } | null) => {
        if (!academyId || !user) {
          set({ existingStudents: [], selfStudent: null });
          return;
        }
        const userFirst = (user.firstName ?? '').toLowerCase().trim();
        const userLast = (user.lastName ?? '').toLowerCase().trim();
        const userFullName = `${userFirst} ${userLast}`.trim();

        try {
          const res = await studentService.getStudents(academyId);
          const all = res.data || [];
          const selfStudent = all.find((s) => s.isSelf === true) ?? null;
          const childrenOnly = all.filter((s) => {
            if (s.isSelf === true) return false;
            const studentFull =
              `${(s.firstName ?? '').toLowerCase()} ${(s.lastName ?? '').toLowerCase()}`.trim();
            const matchesUser =
              userFullName &&
              (studentFull === userFullName || (s.firstName ?? '').toLowerCase() === userFirst);
            return !matchesUser;
          });
          set({ existingStudents: childrenOnly, selfStudent });
        } catch {
          set({ existingStudents: [], selfStudent: null });
        }
      },

      fetchWaitlistData: async (academyId, userId) => {
        if (!academyId || !userId) return;
        try {
          const [studentsRes, entriesRes] = await Promise.all([
            studentService.getStudents(academyId),
            waitlistService.getMyWaitlistEntries(academyId),
          ]);
          set({
            waitlistStudents: studentsRes.data || [],
            myWaitlistEntries: (entriesRes.data || []).map(
              (e: { lessonId: string; id: string }) => ({ lessonId: e.lessonId, id: e.id })
            ),
          });
        } catch {
          set({ waitlistStudents: [], myWaitlistEntries: [] });
        }
      },

      fetchLessonStudentEnrollments: async (lessonId) => {
        try {
          const res = await publicBookingService.getLessonStudentEnrollments(lessonId);
          if (res.success && res.data?.enrollments) {
            set({ lessonStudentEnrollments: res.data.enrollments });
          } else {
            set({ lessonStudentEnrollments: [] });
          }
        } catch {
          set({ lessonStudentEnrollments: [] });
        }
      },
    }),
    {
      name: 'public-booking-wizard',
      storage: createJSONStorage(() => sessionStorage),
      /** Prefer in-memory invitation from URL (`initialize`) over stale persisted null after rehydrate. */
      merge: (persisted, current) => {
        const p =
          persisted && typeof persisted === 'object'
            ? (persisted as Partial<PublicBookingPersistedSlice>)
            : {};
        return {
          ...current,
          ...p,
          invitationToken: current.invitationToken ?? p.invitationToken ?? null,
          manualInviteToken: current.manualInviteToken ?? p.manualInviteToken ?? null,
        };
      },
      partialize: (state) => ({
        academySlug: state.academySlug,
        lessonId: state.lessonId,
        userType: state.userType,
        selectedSessionDates: state.selectedSessionDates,
        campBookingMode: state.campBookingMode,
        groupBookingMode: state.groupBookingMode,
        booking: state.booking,
        allBookings: state.allBookings,
        userFormData: state.userFormData,
        paymentWasMade: state.paymentWasMade,
        waitlistClaimEntryId: state.waitlistClaimEntryId,
        invitationToken: state.invitationToken,
        manualInviteToken: state.manualInviteToken,
      }),
    }
  )
);
